import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  RefreshCw,
  Package,
  AlertTriangle,
  ChevronDown,
  MoreVertical,
  Edit2,
  Trash2,
  History,
  TrendingDown,
  X,
  Loader2,
  Filter,
  ArrowUpDown,
  PackagePlus,
  ChevronUp,
  Tag,
  Building2,
  Upload,
  Download,
  CheckCircle2,
  AlertCircle,
  FileText,
} from 'lucide-react';
import toast from 'react-hot-toast';
import InventoryItemForm from './InventoryItemForm';
import StockUpdateModal from './StockUpdateModal';
import inventoryitemService from "../../../../services/inventoryitemService";
import branchService from '../../../../services/branchService';
import { useAuth } from '../../../../context/AuthContext';
import uomService from '@/services/uomService';
import { ConfirmDialog, AlertDialog } from "@/components/Admin/Menu_Management/UIComponents";
import InventoryCategoryModal from './InventoryCategoryModal';
import ImportFromMenuModal from './ImportFromMenuModal';
import { P } from '@/config/permissionCodes';
import KeyboardShortcutsModal from './KeyboardShortcutsModal';

/**
 * Inventory Item List Component
 *
 * Table view for managing inventory items with:
 * - Search and filter functionality
 * - Stock level indicators
 * - Add/Edit/Delete/Update Stock actions
 */

// Stock status options
const STOCK_STATUS = [
  { value: 'all', label: 'All Items' },
  { value: 'low', label: 'Low Stock' },
  { value: 'out', label: 'Out of Stock' },
  { value: 'healthy', label: 'Healthy Stock' },
];

// Mock UOM data
const MOCK_UOMS = [
  { id: 1, name: 'Kilogram', symbol: 'kg' },
  { id: 2, name: 'Gram', symbol: 'gm' },
  { id: 3, name: 'Liter', symbol: 'L' },
  { id: 4, name: 'Milliliter', symbol: 'ml' },
  { id: 5, name: 'Piece', symbol: 'pc' },
];

function AccessDenied() {
  return (
    <div className="flex items-center justify-center h-full py-24">
      <div className="text-center">

        <h3 className="text-lg font-semibold text-slate-900 mb-2">Access Denied</h3>
        <p className="text-sm text-slate-500">You don't have permission to view inventory items.</p>
      </div>
    </div>
  );
}

export default function InventoryItemList() {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const isOwner = user?.userType === 'SUPER_USER';

  // Permission variables must be defined before useEffect
  const canViewScreen    = hasPermission(P.INVENTORY.ITEMS.SCREEN);
  const canAdd           = hasPermission(P.INVENTORY.ITEMS.ADD);
  const canEdit          = hasPermission(P.INVENTORY.ITEMS.EDIT);
  const canDelete        = hasPermission(P.INVENTORY.ITEMS.DELETE);
  const canUpdateStock   = hasPermission(P.INVENTORY.ITEMS.UPDATE_STOCK);
  const canManageCats =
    hasPermission(P.INVENTORY.ITEMS.CATEGORIES.ADD) ||
    hasPermission(P.INVENTORY.ITEMS.CATEGORIES.EDIT) ||
    hasPermission(P.INVENTORY.ITEMS.CATEGORIES.DELETE) ||
    hasPermission(P.INVENTORY.ITEMS.CATEGORIES.TOGGLE);

  // Keyboard shortcut for Add Item (Ctrl+I)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+I or Alt+I or F2 (choose your preferred shortcut)
      if ((e.ctrlKey && e.key.toLowerCase() === 'i') || e.key === 'F2') {
        e.preventDefault();
        if (canAdd) handleAddItem();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canAdd]);


  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const [openCategoryModal, setOpenCategoryModal] = useState(false);
  const [categories, setCategories] = useState([]);

  const [items, setItems] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showShortcuts, setShowShortcuts] = useState(false);

  if (!canViewScreen) return <AccessDenied />;


  useEffect(() => {
    if (!isOwner) return;
    branchService.getAllBranches().then(result => {
      if (result && result.length > 1) setBranches(result);
    }).catch(console.error);
  }, [isOwner]);

  // Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [stockStatus, setStockStatus] = useState('all');
  const [sortConfig, setSortConfig] = useState({
  key: null,
  direction: "desc",
});


  // Dropdown State
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);

  // Refs for dropdowns
  const categoryRef = useRef(null);
  const statusRef = useRef(null);
  const sortRef = useRef(null);
  const menuRef = useRef(null);

  // Modal State
  const [formOpen, setFormOpen] = useState(false);
  const [stockUpdateOpen, setStockUpdateOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [showBulkUploadDialog, setShowBulkUploadDialog] = useState(false);
  const [showImportFromMenu, setShowImportFromMenu] = useState(false);
  const [bulkFile, setBulkFile]                       = useState(null);
  const [isUploading, setIsUploading]                 = useState(false);
  const [bulkUploadResult, setBulkUploadResult]     = useState(null);
  const [showBulkDeductDialog, setShowBulkDeductDialog] = useState(false);
  const [deductFile, setDeductFile]                     = useState(null);
  const [showUploadsModal, setShowUploadsModal]         = useState(false);
  const [uploadedFiles, setUploadedFiles]               = useState([]);
  const [isLoadingUploads, setIsLoadingUploads]         = useState(false);

  const fetchItems = async () => {
    try {
      setIsLoading(true);
      const filters = {};
      if (selectedBranchId) filters.branchId = selectedBranchId;
      const data = await inventoryitemService.getAll(filters);
      const itemsArray = Array.isArray(data.data) ? data.data : data;
      const mapped = itemsArray.map((item) => ({
        id: item.id,
        name: item.name,
        categoryId: item.idCategory?.id ?? item.categoryId ?? null,
        categoryName: item.idCategory?.name ?? item.category?.name ?? "N/A",
        idUom:
          item.uom?.id ??
          item.unitOfMeasurement?.id ??
          item.idUom?.id ??
          item.idUom ??
          null,
          formattedStock: item.formattedStock,
        currentStock: item.currentStock ?? item.current_stock ?? 0,
        minimumStock: item.minimumStock ?? item.minimum_stock ?? 0,
        unitCost: item.unitCost ?? item.unit_cost ?? 0,
        isActive: item.isActive ?? true,
        isDeleted: item.isDeleted ?? false,
      }));
      setItems(mapped);
    } catch (error) {
      console.error('Failed to load inventory items:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Alias for compatibility with ImportFromMenuModal onSuccess
  const loadItems = fetchItems;

  useEffect(() => {
    fetchItems();
  }, [selectedBranchId]);

//Load categories
useEffect(() => {
  loadCategories();
}, [selectedBranchId]);


  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target)) {
        setShowCategoryDropdown(false);
      }
      if (statusRef.current && !statusRef.current.contains(e.target)) {
        setShowStatusDropdown(false);
      }
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setShowSortDropdown(false);
      }
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

//UOM Dropdown

const fetchUomList = async () => {
  try {
    const data = await uomService.getAll();
    setUoms(Array.isArray(data) ? data : []);
  } catch (error) {
    console.error("Error loading UOMs:", error);
  }
};

  useEffect(() => {
    fetchUomList();
  }, []);

useEffect(() => {
  if (openCategoryModal) loadCategories();
}, [openCategoryModal]);

const loadCategories = async () => {
  try {
    const data = await inventoryitemService.getAllCategories(selectedBranchId);

    console.log(" CATEGORY API RAW DATA:", data);

    setCategories(Array.isArray(data) ? data : []);
  } catch (err) {
    console.error("Error loading categories:", err);
    setCategories([]);
  }
};

  // Get UOM symbol by ID
  const getUomSymbol = (idUom) => {
    const uom = uoms.find(u => u.id === idUom);
    return uom?.symbol || '';
  };

  // Get stock status for an item
  const getStockStatus = (item) => {
    if (item.currentStock <= 0) return 'out';
    if (item.currentStock <= item.minimumStock) return 'low';
    return 'healthy';
  };
 const handleColumnSort = (key) => {
  setSortConfig((prev) => {
    if (prev.key === key) {
      return {
        key,
        direction: prev.direction === "asc" ? "desc" : "asc",
      };
    }
    return { key, direction: "desc" };
    });
};

  // Filter and sort items
  const filteredItems = useMemo(() => {
    let result = items.filter(item => item.isActive);

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item =>
        item.name?.toLowerCase().includes(query) ||
        item.categoryName?.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      result = result.filter(
        item => item.categoryName === selectedCategory
      );
    }

    // Stock status filter
    if (stockStatus !== 'all') {
      result = result.filter(
        item => getStockStatus(item) === stockStatus
      );
    }

    // Sort
if (sortConfig.key) {
    result = [...result].sort((a, b) => {
    const A = a[sortConfig.key] ?? "";
    const B = b[sortConfig.key] ?? "";

    if (typeof A === "string") {
      return sortConfig.direction === "asc"
        ? A.localeCompare(B)
        : B.localeCompare(A);
    }

    return sortConfig.direction === "asc" ? A - B : B - A;
  });
}

    return result;
  }, [items, searchQuery, selectedCategory, stockStatus,sortConfig]);

  // Stats
  const stats = useMemo(() => {
    const activeItems = items.filter(i => i.isActive);
    return {
      total: activeItems.length,
      lowStock: activeItems.filter(i => getStockStatus(i) === 'low').length,
      outOfStock: activeItems.filter(i => getStockStatus(i) === 'out').length,
    };
  }, [items]);

  // Handlers
  const handleRefresh = () => {
    fetchItems();
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await inventoryitemService.downloadTemplate();
      const blob = response instanceof Blob ? response : new Blob([response.data ?? response], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Bulk_Upload_Inventory_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast('Template downloaded successfully', 'success');
    } catch (error) {
      console.error('Failed to download template:', error);
      showToast('Failed to download template. Please try again.', 'error');
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      showToast('Please select a file to upload.', 'error');
      return;
    }
    setIsUploading(true);
    try {
      const response = await inventoryitemService.bulkUpload(bulkFile);
      const resData = response?.data || response;

      setBulkUploadResult(resData);
      setShowBulkUploadDialog(false);
      setBulkFile(null);
      await fetchItems();
    } catch (error) {
      console.error('Bulk upload failed:', error);
      showToast('Failed to upload bulk inventory.', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleBulkDeduct = async () => {
    if (!deductFile) {
      showToast('Please select a file to upload.', 'error');
      return;
    }
    setIsUploading(true);
    try {
      const response = await inventoryitemService.bulkUpload(deductFile, 'deduct');
      const resData = response?.data || response;

      setBulkUploadResult(resData);
      setShowBulkDeductDialog(false);
      setDeductFile(null);
      await fetchItems();
    } catch (error) {
      console.error('Bulk deduct failed:', error);
      showToast('Failed to process stock deduction.', 'error');
    } finally {
      setIsUploading(false);
    }
  };

const handleAddItem = async () => {
    if (!canAdd) return;
    await loadCategories();
    setSelectedItem(null);
    setFormOpen(true);
  };

  // Edit → use GET /api/inventory/items/{id}
  const handleEditItem = async (item) => {
    if (!canEdit) return;
    try {
      setOpenMenuId(null);
      setIsLoading(true);
      const data = await inventoryitemService.getById(item.id);

 const mapped = {
  id: data.id,
  name: data.name,
  categoryId: Number(data.idCategory?.id ?? data.categoryId) || null,
  category: data.category,
  idUom:
    data.uom?.id ??
    data.unitOfMeasurement?.id ??
    data.idUom?.id ??
    data.idUom ??
    null,
  currentStock: data.currentStock ?? 0,
  minimumStock: data.minimumStock ?? 0,
  unitCost: data.unitCost ?? 0,
  isActive: data.isActive ?? true,
  isDeleted: data.isDeleted ?? false,
};


      setSelectedItem(mapped);
      setFormOpen(true);
    } catch (error) {
      console.error('Failed to load item details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStock = (item) => {
    if (!canUpdateStock) return;

    setSelectedItem(item);
    setOpenMenuId(null);
    setStockUpdateOpen(true);
  };

  // View History → navigates to the dedicated Inventory Item History page
  // (replaces the old Item History modal)
  const handleViewHistory = (item) => {
    setOpenMenuId(null);
    navigate(`/inventory/items/${item.id}/history`);
  };

  // DELETE
const handleDeleteItem = (item) => {
  if (!canDelete) return;
  setConfirmDialog({
    isOpen: true,
    title: "Delete Inventory Item",
    message: `Are you sure you want to delete "${item.name}"? This action cannot be undone.`,
    onConfirm: () => confirmDelete(item),
  });
};

const confirmDelete = async (item) => {
  try {
    await inventoryitemService.delete(item.id);

    setItems(prev => prev.filter(i => i.id !== item.id));

    showToast("Item deleted successfully", "success");

    setOpenMenuId(null);
    setConfirmDialog({ isOpen: false });
  } catch (error) {
    console.error("Failed to delete item:", error);
    showToast("Failed to delete item", "error");
  }
};

  //POST and
  const handleSaveItem = async (savedItem) => {
    try {
      setIsLoading(true);

      const payload = {
        id: savedItem.id || 0,
        name: savedItem.name,
        taxId: savedItem.taxId ?? null,
        currentStock: savedItem.currentStock,
        minimumStock: savedItem.minimumStock,
        unitCost: savedItem.unitCost,
        isActive: savedItem.isActive ?? true,
        isDeleted: savedItem.isDeleted ?? false,
        idCategory: { id: savedItem.categoryId },
        idUom: {
          id: savedItem.idUom,
        },
      };

      if (savedItem.id) {
      // UPDATE ITEM
        await inventoryitemService.update(savedItem.id, payload);
        showToast("Item updated successfully", "success");
      } else {
      // ADD NEW ITEM
        await inventoryitemService.create(payload);
        showToast("Item added successfully", "success");
    }

      setFormOpen(false);
      setSelectedItem(null);

      await fetchItems();
    } catch (error) {
      console.error('Failed to save item:', error);
      showToast("Failed to save item", "error");
    } finally {
      setIsLoading(false);
    }
  };

 const showToast = (message, type = "success") => {
  if (type === "success") toast.success(message);
  else if (type === "error") toast.error(message);
  else toast(message);
};

const handleStockUpdate = async () => {
  try {
    await fetchItems();

    showToast("Stock updated successfully", "success");

  } catch (err) {
    console.error("Failed to refresh items:", err);
    showToast("Failed to update stock", "error");
  }

    setStockUpdateOpen(false);
    setSelectedItem(null);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setStockStatus('all');
  };

  const handleViewDeductUploads = async () => {
    setIsLoadingUploads(true);
    try {
      const data = await inventoryitemService.getUploadedFiles('deduct');
      setUploadedFiles(Array.isArray(data) ? data : []);
      setShowUploadsModal(true);
    } catch (error) {
      console.error('Failed to fetch uploaded files:', error);
      showToast('Failed to load uploaded files', 'error');
    } finally {
      setIsLoadingUploads(false);
    }
  };

  const handleDownloadUploadedFile = async (file) => {
    try {
      const response = await inventoryitemService.downloadUploadedFile(file.id);
      const blob = response instanceof Blob ? response : new Blob([response.data ?? response], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.originalFileName || 'download.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast('File downloaded successfully', 'success');
    } catch (error) {
      console.error('Failed to download file:', error);
      showToast('Failed to download file', 'error');
    }
  };

  const hasActiveFilters = searchQuery || selectedCategory !== 'all' || stockStatus !== 'all';
  const hasAnyRowAction = canEdit || canUpdateStock || canDelete;


  return (
    <div className="flex flex-col h-full bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-semibold text-slate-900">Inventory Items</h1>
            {/* Quick Stats */}
            <div className="hidden md:flex items-center gap-3 text-sm">
              <span className="text-slate-500">{stats.total} items</span>
              {stats.lowStock > 0 && (
                <span className="flex items-center gap-1 text-amber-600">
                  <AlertTriangle size={14} />
                  {stats.lowStock} low
                </span>
              )}
              {stats.outOfStock > 0 && (
                <span className="flex items-center gap-1 text-red-600">
                  <Package size={14} />
                  {stats.outOfStock} out
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">

            {branches.length > 1 && (
              <div className="relative">
                <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <select
                  value={selectedBranchId ?? ''}
                  onChange={(e) => { setSelectedBranchId(e.target.value || null); setSelectedCategory('all'); }}
                  className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
                >
                  <option value="">All Branches</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            )}

            {/* Keyboard Shortcuts Icon - left of Manage Categories, icon only */}
            <button
              className="p-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 flex items-center justify-center shadow-sm"
              style={{ boxShadow: '0 1px 2px 0 rgba(16,30,54,.04)' }}
              title="Keyboard Shortcuts"
              onClick={() => setShowShortcuts(true)}
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><rect x="2.5" y="5" width="15" height="10" rx="2" stroke="#64748b" strokeWidth="1.5"/><rect x="5.5" y="8" width="1.5" height="1.5" rx=".5" fill="#64748b"/><rect x="8.25" y="8" width="1.5" height="1.5" rx=".5" fill="#64748b"/><rect x="11" y="8" width="1.5" height="1.5" rx=".5" fill="#64748b"/><rect x="13.75" y="8" width="1.5" height="1.5" rx=".5" fill="#64748b"/><rect x="5.5" y="11" width="9.75" height="1.5" rx=".75" fill="#64748b"/></svg>
            </button>

            {canManageCats && (
              <button
                onClick={() => {
                  if (!canManageCats) {
                    toast.error("No permission");
                    return;
                  }
                  setOpenCategoryModal(true);
                }}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm font-medium transition-colors"
              >
                <Tag size={16} className="text-gray-700" />
                <span>Manage Categories</span>
              </button>
            )}

            {canAdd && (
              <>
                <button
                  onClick={() => setShowImportFromMenu(true)}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2 text-sm font-medium transition-colors"
                >
                  <Download size={16} />
                  <span>Import from Menu</span>
                </button>
                <button
                  onClick={() => setShowBulkDeductDialog(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 text-sm font-medium transition-colors"
                >
                  <Upload size={16} />
                  <span>Upload Sales Data</span>
                </button>
                <button
                  onClick={() => setShowBulkUploadDialog(true)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 text-sm font-medium transition-colors"
                >
                  <Upload size={16} />
                  <span>Bulk Upload</span>
                </button>
                {/* Keyboard Shortcuts Icon Button */}

                <button
                  onClick={handleAddItem}
                  title="Add Item (Ctrl+I or F2)"
                  className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium text-sm"
                >
                  <Plus size={16} />
                  Add Item
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-2.5">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-8 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Category Filter */}
          <div className="relative" ref={categoryRef}>
            <button
              onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-slate-600 hover:bg-slate-100 border border-slate-200"
            >
              <Filter size={14} />
              {selectedCategory === 'all' ? 'Category' : selectedCategory}
              <ChevronDown size={14} className={`transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showCategoryDropdown && (
              <div className="absolute left-0 top-full mt-1 w-44 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1">
                <button
                  onClick={() => { setSelectedCategory('all'); setShowCategoryDropdown(false); }}
                  className={`w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50 ${
                    selectedCategory === 'all' ? 'text-teal-600 bg-teal-50' : 'text-slate-700'
                  }`}
                >
                  All Categories
                </button>
             {categories.map(cat => (
  <button
    key={cat.id}
    onClick={() => {
      setSelectedCategory(cat.name);
      setShowCategoryDropdown(false);
    }}
    className={`w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50 ${
      selectedCategory === cat.name
        ? "text-teal-600 bg-teal-50"
        : "text-slate-700"
    }`}
  >
    {cat.name}
  </button>
))}

              </div>
            )}
          </div>

          {/* Stock Status Filter */}
          <div className="relative" ref={statusRef}>
            <button
              onClick={() => setShowStatusDropdown(!showStatusDropdown)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-slate-600 hover:bg-slate-100 border border-slate-200"
            >
              <Package size={14} />
              {STOCK_STATUS.find(s => s.value === stockStatus)?.label || 'Stock Status'}
              <ChevronDown size={14} className={`transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showStatusDropdown && (
              <div className="absolute left-0 top-full mt-1 w-40 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1">
                {STOCK_STATUS.map(status => (
                  <button
                    key={status.value}
                    onClick={() => { setStockStatus(status.value); setShowStatusDropdown(false); }}
                    className={`w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50 ${
                      stockStatus === status.value ? 'text-teal-600 bg-teal-50' : 'text-slate-700'
                    }`}
                  >
                    {status.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Sort Dropdown */}
          <div className="relative" ref={sortRef}>
             <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
            </button>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-2.5 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 size={40} className="text-teal-500 animate-spin mb-4" />
            <p className="text-slate-500">Loading inventory items...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mb-6">
              <Package size={40} className="text-slate-400" />
            </div>
            {items.filter(i => i.isActive).length === 0 ? (
              <>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No Inventory Items</h3>
                <p className="text-slate-500 mb-6 max-w-md">
                  Start by adding your raw materials and ingredients to track stock levels.
                </p>
                <button
                  onClick={handleAddItem}
                  title="Add Item (Ctrl+I or F2)"
                  className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
                >
                  <Plus size={18} />
                  Add First Item
                </button>
              </>
            ) : (
              <>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No Matching Items</h3>
                <p className="text-slate-500 mb-4">Try adjusting your filters.</p>
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-sm text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                >
                  Clear All Filters
                </button>
              </>
            )}
          </div>
        ) : (
          <table className="w-full">
<thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
  <tr>

    {/* ITEM NAME */}
    <th
      onClick={() => handleColumnSort("name")}
      className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer select-none
        ${sortConfig.key === "name" ? "text-teal-600" : "text-slate-600"}`}
    >
      <div className="flex items-center gap-1 group">
        Item Name
        {sortConfig.key !== "name" ? (
          <ArrowUpDown size={14} className="opacity-40 group-hover:opacity-70" />
        ) : sortConfig.direction === "asc" ? (
          <ChevronUp size={14} />
        ) : (
          <ChevronDown size={14} />
        )}
      </div>
    </th>

    {/* CATEGORY */}
    <th
      onClick={() => handleColumnSort("categoryName")}
      className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer select-none
        ${sortConfig.key === "categoryName" ? "text-teal-600" : "text-slate-600"}`}
    >
      <div className="flex items-center gap-1 group">
        Category
        {sortConfig.key !== "categoryName" ? (
          <ArrowUpDown size={14} className="opacity-40 group-hover:opacity-70" />
        ) : sortConfig.direction === "asc" ? (
          <ChevronUp size={14} />
        ) : (
          <ChevronDown size={14} />
        )}
      </div>
    </th>

    {/* UOM */}
    <th
      onClick={() => handleColumnSort("idUom")}
      className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider cursor-pointer select-none
        ${sortConfig.key === "idUom" ? "text-teal-600" : "text-slate-600"}`}
    >
      <div className="flex items-center justify-center gap-1 group">
        UOM
        {sortConfig.key !== "idUom" ? (
          <ArrowUpDown size={14} className="opacity-40 group-hover:opacity-70" />
        ) : sortConfig.direction === "asc" ? (
          <ChevronUp size={14} />
        ) : (
          <ChevronDown size={14} />
        )}
      </div>
    </th>

    {/* CURRENT STOCK */}
    <th
      onClick={() => handleColumnSort("currentStock")}
      className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider cursor-pointer select-none
        ${sortConfig.key === "currentStock" ? "text-teal-600" : "text-slate-600"}`}
    >
      <div className="flex items-center justify-end gap-1 group">
        Current Stock
        {sortConfig.key !== "currentStock" ? (
          <ArrowUpDown size={14} className="opacity-40 group-hover:opacity-70" />
        ) : sortConfig.direction === "asc" ? (
          <ChevronUp size={14} />
        ) : (
          <ChevronDown size={14} />
        )}
      </div>
    </th>

    {/* MIN STOCK */}
    <th
      onClick={() => handleColumnSort("minimumStock")}
      className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider cursor-pointer select-none
        ${sortConfig.key === "minimumStock" ? "text-teal-600" : "text-slate-600"}`}
    >
      <div className="flex items-center justify-end gap-1 group">
        Min Stock
        {sortConfig.key !== "minimumStock" ? (
          <ArrowUpDown size={14} className="opacity-40 group-hover:opacity-70" />
        ) : sortConfig.direction === "asc" ? (
          <ChevronUp size={14} />
        ) : (
          <ChevronDown size={14} />
        )}
      </div>
    </th>

    {/* UNIT COST */}
    <th
      onClick={() => handleColumnSort("unitCost")}
      className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider cursor-pointer select-none
        ${sortConfig.key === "unitCost" ? "text-teal-600" : "text-slate-600"}`}
    >
      <div className="flex items-center justify-end gap-1 group">
        Unit Cost
        {sortConfig.key !== "unitCost" ? (
          <ArrowUpDown size={14} className="opacity-40 group-hover:opacity-70" />
        ) : sortConfig.direction === "asc" ? (
          <ChevronUp size={14} />
        ) : (
          <ChevronDown size={14} />
        )}
      </div>
    </th>

    {/* ACTIONS — not sortable */}
   {hasAnyRowAction && (
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">
                    Actions
                  </th>
                )}
  </tr>
</thead>

            <tbody className="divide-y divide-slate-100 bg-white">
              {filteredItems.map((item) => {
                const stockStatus = getStockStatus(item);
                const stockPercent = item.minimumStock > 0
                  ? Math.min(100, (item.currentStock / (item.minimumStock * 2)) * 100)
                  : 100;
                  const rowHasActions = canEdit || canUpdateStock || canDelete;


                return (
                  <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                    {/* Name — clickable: navigates to the item's Stock
                        Movement History (same target as the Actions menu's
                        View History; both go through handleViewHistory so
                        the two entry points can't drift apart). */}
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        {stockStatus === 'out' && (
                          <span className="flex-shrink-0 w-2 h-2 rounded-full bg-red-500" title="Out of Stock" />
                        )}
                        {stockStatus === 'low' && (
                          <AlertTriangle size={14} className="flex-shrink-0 text-amber-500" title="Low Stock" />
                        )}
                        <button
                          onClick={() => handleViewHistory(item)}
                          className="font-medium text-slate-900 text-left cursor-pointer hover:text-teal-600 hover:underline underline-offset-2 transition-colors focus:outline-none focus:text-teal-600"
                          title={`View stock movement history for ${item.name}`}
                        >
                          {item.name}
                        </button>
                      </div>
                    </td>

                    {/* Category */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium `}>
                        {item.categoryName || '-'}
                      </span>
                    </td>

                    {/* UOM */}
                    <td className="px-4 py-3 text-center text-sm text-slate-600">
                      {getUomSymbol(item.idUom)}
                    </td>

                    {/* Current Stock */}
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-end gap-1">
                        <span className={`text-sm font-semibold ${
                          stockStatus === 'out' ? 'text-red-600' :
                          stockStatus === 'low' ? 'text-amber-600' : 'text-slate-900'
                        }`}>
                          {item.formattedStock || item.currentStock.toFixed(2)}
                        </span>
                        <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              stockStatus === 'out' ? 'bg-red-500' :
                              stockStatus === 'low' ? 'bg-amber-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${stockPercent}%` }}
                          />
                        </div>
                      </div>
                    </td>

                    {/* Min Stock */}
                    <td className="px-4 py-3 text-right text-sm text-slate-500">
                      {item.minimumStock.toFixed(2)}
                    </td>

                    {/* Unit Cost */}
                    <td className="px-4 py-3 text-right text-sm font-medium text-slate-900">
                      ₹{item.unitCost.toFixed(2)}
                    </td>

                    {/* Actions */}
                   {hasAnyRowAction && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center relative" ref={openMenuId === item.id ? menuRef : null}>
                          {rowHasActions && (
                            <button
                              onClick={() => setOpenMenuId(openMenuId === item.id ? null : item.id)}
                              className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors"
                            >
                              <MoreVertical size={16} />
                            </button>
                          )}

                        {openMenuId === item.id && (
                            <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-slate-200 rounded-lg shadow-lg z-30 py-1 overflow-hidden">
                              {canEdit && (
                                <button
                                  onClick={() => handleEditItem(item)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                                >
                                  <Edit2 size={14} className="text-teal-500" />
                                  Edit Item
                                </button>
                              )}
                              {canUpdateStock && (
                                <button
                                  onClick={() => handleUpdateStock(item)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                                >
                                  <PackagePlus size={14} className="text-blue-500" />
                                  Update Stock
                                </button>
                              )}
                             <button
                                onClick={() => handleViewHistory(item)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                              >
                                <History size={14} className="text-purple-500" />
                                View History
                              </button>
                              {canDelete && (
                                <>
                                  <div className="border-t border-slate-100 my-1" />
                                  <button
                                    onClick={() => handleDeleteItem(item)}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                                  >
                                    <Trash2 size={14} />
                                    Delete
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      {!isLoading && filteredItems.length > 0 && (
        <div className="bg-white border-t border-slate-200 px-6 py-2">
          <p className="text-xs text-slate-500">
            {hasActiveFilters
              ? `Showing ${filteredItems.length} of ${items.filter(i => i.isActive).length} items`
              : `${filteredItems.length} item${filteredItems.length !== 1 ? 's' : ''}`
            }
          </p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {formOpen && (canAdd || canEdit) && (
        <InventoryItemForm
          item={selectedItem}
          uoms={uoms}
          categories={categories.map(cat => ({id: cat.id,name: cat.name}))}
          onSave={handleSaveItem}
          onClose={() => setFormOpen(false)}
        />
      )}

      <KeyboardShortcutsModal
        open={showShortcuts}
        onClose={() => setShowShortcuts(false)}
      />

      {/* Stock Update Modal */}
      {stockUpdateOpen && selectedItem && canUpdateStock && (
        <StockUpdateModal
          item={selectedItem}
          uomSymbol={getUomSymbol(selectedItem.idUom)}
          onSave={handleStockUpdate}
          onClose={() => { setStockUpdateOpen(false); setSelectedItem(null); }}
        />
      )}

<ConfirmDialog
  isOpen={confirmDialog.isOpen}
  title={confirmDialog.title}
  message={confirmDialog.message}
  confirmText="Delete"
  type="danger"
  onConfirm={() => {
    confirmDialog.onConfirm();
    setConfirmDialog({ isOpen: false });
  }}
  onClose={() => setConfirmDialog({ isOpen: false })}
 />

{openCategoryModal && canManageCats && (
<InventoryCategoryModal
  categories={categories}
  setCategories={setCategories}
  onClose={() => setOpenCategoryModal(false)}
  onSave={async () => {
    await loadCategories();
    setOpenCategoryModal(false);
  }}
/>
)}

        {showBulkUploadDialog && canAdd && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg"><Upload className="w-5 h-5 text-indigo-600" /></div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Bulk Upload Inventory</h2>
                    <p className="text-xs text-gray-500">Create or update inventory items from Excel</p>
                  </div>
                </div>
                <button onClick={() => { setShowBulkUploadDialog(false); setBulkFile(null); }} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-6 flex flex-col gap-6">
                <div className="flex items-center justify-between border-b pb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">1. Download Template</p>
                    <p className="text-xs text-gray-500 mt-1">Get the exact format needed for upload</p>
                  </div>
                  <button
                    onClick={handleDownloadTemplate}
                    className="px-3 py-1.5 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-gray-200 transition-colors"
                  >
                    <Download size={16} />
                    <span>Download</span>
                  </button>
                </div>

                <div className="flex flex-col gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-700">2. Upload Filled Template</p>
                    <p className="text-xs text-gray-500 mt-1">Select your completed Excel file to import items</p>
                  </div>
                  <div className="mt-2">
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      onChange={(e) => setBulkFile(e.target.files[0])}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 border border-gray-300 rounded-lg cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                <button
                  onClick={() => { setShowBulkUploadDialog(false); setBulkFile(null); }}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm transition-colors"
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkUpload}
                  disabled={isUploading || !bulkFile}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm flex items-center gap-2 transition-colors"
                >
                  {isUploading ? <RefreshCw size={16} className="animate-spin" /> : <Upload size={16} />}
                  <span>{isUploading ? 'Uploading...' : 'Upload Data'}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Deduct Dialog */}
        {showBulkDeductDialog && canAdd && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-red-100 bg-red-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg"><TrendingDown className="w-5 h-5 text-red-600" /></div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Upload Sales Data</h2>
                    <p className="text-xs text-gray-500">Upload sales data to deduct from inventory stock</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleViewDeductUploads}
                    disabled={isLoadingUploads}
                    className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs font-medium flex items-center gap-1.5 transition-colors"
                    title="View previously uploaded files"
                  >
                    {isLoadingUploads ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                    <span>View Uploads</span>
                  </button>
                  <button onClick={() => { setShowBulkDeductDialog(false); setDeductFile(null); }} className="p-2 hover:bg-red-100 rounded-lg transition-colors">
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>

              <div className="p-6 flex flex-col gap-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-700">
                    Upload an Excel file containing item names and sold quantities to deduct from current stock.
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Select Excel File</p>
                    <p className="text-xs text-gray-500 mt-1">Supports .xlsx and .xls files</p>
                  </div>
                  <div className="mt-2">
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      onChange={(e) => setDeductFile(e.target.files[0])}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100 border border-gray-300 rounded-lg cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                <button
                  onClick={() => { setShowBulkDeductDialog(false); setDeductFile(null); }}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm transition-colors"
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkDeduct}
                  disabled={isUploading || !deductFile}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm flex items-center gap-2 transition-colors"
                >
                  {isUploading ? <RefreshCw size={16} className="animate-spin" /> : <TrendingDown size={16} />}
                  <span>{isUploading ? 'Processing...' : 'Upload & Deduct'}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Uploaded Files Modal */}
        {showUploadsModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[80vh] border border-slate-200">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <FileText size={20} className="text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Upload History</h3>
                    <p className="text-xs text-slate-500">{uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} uploaded</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowUploadsModal(false)}
                  className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  <X size={20} className="text-slate-500" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                {uploadedFiles.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                      <FileText size={32} className="text-slate-400" />
                    </div>
                    <p className="text-slate-500 text-sm">No deduct files uploaded yet</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">File Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Uploaded On</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Results</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {uploadedFiles.map((file) => (
                        <tr key={file.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <FileText size={16} className="text-red-500 shrink-0" />
                              <span className="text-sm font-medium text-slate-900 truncate max-w-[200px]" title={file.originalFileName}>
                                {file.originalFileName}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500">
                            {file.createdDateTime
                              ? new Date(file.createdDateTime).toLocaleString('en-IN', {
                                  day: '2-digit', month: 'short', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit', hour12: true
                                })
                              : 'N/A'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-2 text-xs">
                              <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded font-medium">
                                {file.successCount ?? 0} ✓
                              </span>
                              <span className="px-1.5 py-0.5 bg-red-50 text-red-700 rounded font-medium">
                                {file.failureCount ?? 0} ✗
                              </span>
                              {(file.skippedCount ?? 0) > 0 && (
                                <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded font-medium">
                                  {file.skippedCount} skipped
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleDownloadUploadedFile(file)}
                              className="p-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors"
                              title="Download this file"
                            >
                              <Download size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end">
                <button
                  onClick={() => setShowUploadsModal(false)}
                  className="px-6 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 font-medium text-sm transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {bulkUploadResult && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999] animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-scaleIn border border-slate-200">
              <div className={`px-6 py-4 border-b flex items-center justify-between ${bulkUploadResult.failureCount === 0 || bulkUploadResult.failureCount === undefined ? 'bg-emerald-50/50' : 'bg-red-50/50'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bulkUploadResult.failureCount === 0 || bulkUploadResult.failureCount === undefined ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                    {bulkUploadResult.failureCount === 0 || bulkUploadResult.failureCount === undefined ? <CheckCircle2 size={24} /> : <AlertCircle size={24} />}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 leading-tight">Bulk Upload Results</h3>
                    <p className={`text-xs font-medium ${bulkUploadResult.failureCount === 0 || bulkUploadResult.failureCount === undefined ? 'text-emerald-600' : 'text-red-600'}`}>
                      {bulkUploadResult.failureCount === 0 || bulkUploadResult.failureCount === undefined ? 'Process completed successfully' : 'Processing completed with some errors'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setBulkUploadResult(null)}
                  className="p-2 hover:bg-white/80 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[70vh]">
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-center gap-4">
                      <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-lg flex items-center justify-center shrink-0 shadow-sm">
                        <CheckCircle2 size={20} />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-0.5">Success</p>
                        <p className="text-xl font-black text-slate-900 leading-none">{bulkUploadResult.successCount || 0}</p>
                      </div>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-center gap-4">
                      <div className="w-10 h-10 bg-red-100 text-red-600 rounded-lg flex items-center justify-center shrink-0 shadow-sm">
                        <AlertCircle size={20} />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-0.5">Failed</p>
                        <p className="text-xl font-black text-slate-900 leading-none">{bulkUploadResult.failureCount || 0}</p>
                      </div>
                    </div>
                  </div>

                  {bulkUploadResult.errors && bulkUploadResult.errors.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs font-extrabold text-red-600 mb-2 flex items-center gap-2 uppercase tracking-tight">
                        <AlertCircle size={14} />
                        Row Processing Errors ({bulkUploadResult.errors.length})
                      </p>
                      <div className="bg-white rounded-xl border border-red-100 overflow-hidden shadow-sm">
                        <div className="max-h-60 overflow-y-auto p-4 bg-red-50 text-xs text-red-600 font-medium">
                           {bulkUploadResult.errors.map((error, idx) => (
                             <div key={idx} className="mb-1 pb-1 border-b border-red-100/50 last:border-0 last:mb-0 last:pb-0">{error}</div>
                           ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="px-6 py-4 bg-slate-50 border-t flex justify-end">
                <button
                  onClick={() => setBulkUploadResult(null)}
                  className="px-8 py-2.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-all font-bold text-sm shadow-md hover:shadow-lg active:scale-95"
                >
                  Close Summary
                </button>
              </div>
            </div>
          </div>
        )}

      {showImportFromMenu && (
        <ImportFromMenuModal
          onClose={() => setShowImportFromMenu(false)}
          onSuccess={() => { setShowImportFromMenu(false); loadItems(); }}
          categories={categories}
          uomList={uoms}
        />
      )}

    </div>
  );
}